// src/components/Layout/Sidebar.js
import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Badge } from "@mui/material";
import {
  Box,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  LayoutDashboard,
  TrendingUp,
  Map as MapIcon,
  Building2,
  Target,
  Search,
  Mail,
  MessagesSquare,
  MessageCircle,
  MapPin,
  Sparkles,
  Settings,
  FileText,
  Package,
  CreditCard,
  ShieldCheck,
  Users,
  BarChart3,
  Share2,
  History,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { userIsLegacyDefaultOrganization } from "../../utils/legacyDefaultOrganization";
import useTour from "../../tour/useTour";
import { TERRITORIES_TOUR_STEPS, TERRITORIES_NEW_STEPS } from "../../tour/steps/territories.steps";
import { LEADS_MAP_TOUR_STEPS, LEADS_MAP_NEW_STEPS } from "../../tour/steps/leads-map.steps";
import { LEADS_MOBILE_MAP_TOUR_STEPS, LEADS_MOBILE_MAP_NEW_STEPS } from "../../tour/steps/leads-mobile-map.steps";
import { SEARCH_LEADS_TOUR_STEPS, SEARCH_LEADS_NEW_STEPS } from "../../tour/steps/search-leads.steps";
import { TEAM_CHAT_TOUR_STEPS, TEAM_CHAT_NEW_STEPS } from "../../tour/steps/team-chat.steps";
import { PRODUCTS_SERVICES_TOUR_STEPS, PRODUCTS_SERVICES_NEW_STEPS } from "../../tour/steps/products-services.steps";
import {
  SALES_PIPELINE_TOUR_STEPS,
  MESSAGE_CENTER_TOUR_STEPS,
  FOLLOW_UP_INTEL_TOUR_STEPS,
  CONVERSATIONS_TOUR_STEPS,
  REP_POSITIONS_TOUR_STEPS,
  CHANGELOG_TOUR_STEPS,
  COMPANY_PROFILE_TOUR_STEPS,
  ROLES_TOUR_STEPS,
  USERS_TOUR_STEPS,
  REPORTS_TOUR_STEPS,
  SUBSCRIPTION_TOUR_STEPS,
  INTEGRATIONS_TOUR_STEPS,
  ACTION_LOG_TOUR_STEPS,
} from "../../tour/steps/sidebar-pages.steps";
import { resolveSettingsTourSteps } from "../../tour/resolveSettingsTourSteps";
import { isSettingsTourComplete, SETTINGS_TOUR_KEY } from "../../tour/tourKeys";
import { OPEN_SETTINGS_FOR_TOUR_EVENT } from "../../guidedTour/pageHelpEvents";
import { getUserInitials } from "../../utils/userInitials";

import { useGlobalCounts } from "../../context/GlobalCountContext";
import useAuth from "../../hooks/useAuth";

const drawerWidth = 260;

/* -------------------------------------------
   ChatGPT-Style Sidebar Spacing + Styling
----------------------------------------------*/
const linkStyle = {
  textDecoration: "none",
  color: "inherit",
  width: "100%",
};

// activeButtonSx and baseItemSx are defined inside the component (theme-aware)


/* -------------------------------------------
   Sidebar Component
----------------------------------------------*/
export default function Sidebar() {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const sidebarBg = isLight ? "#ffffff" : "#1e293b";
  const sidebarText = isLight ? "#1e293b" : "#ffffff";

  const activeButtonSx = {
    bgcolor: alpha(theme.palette.primary.main, isLight ? 0.12 : 0.20),
    borderRadius: "10px",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, isLight ? 0.16 : 0.26),
    },
    "& .MuiListItemIcon-root, & .MuiListItemIcon-root svg, & .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
  };

  const baseItemSx = {
    borderRadius: "10px",
    px: 2,
    py: 1,
    mx: 1,
    mb: 0.5,
    "& .MuiListItemText-primary": {
      fontWeight: 500,
      letterSpacing: "-0.01em",
      color: isLight ? "#475569" : "#cbd5e1",
    },
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, isLight ? 0.06 : 0.10),
    },
  };

  const location = useLocation();
  const { counts } = useGlobalCounts();
const messageCenterUnread =
  (counts?.notifications || 0) +
  (counts?.tasks || 0) +
  (counts?.followups || 0);
  const conversationsUnread = counts?.conversations ?? 0;
  const teamChatUnread = counts?.mentions ?? 0;
  const followUpIntelOverdue = counts?.followup_overdue ?? 0;

  const { role, can, user } = useAuth();
 const navigate = useNavigate();
  const { startTour, isOpen: isTourOpen, endTour } = useTour();

  const [openSettings, setOpenSettings] = useState(false);

  // AUTO-OPEN/CLOSE SETTINGS WHEN NAVIGATING
React.useEffect(() => {
  const settingsPaths = [
    "/settings",
    "/settings/roles",
    "/settings/theme",
    "/settings/reports",
    "/settings/rep-positions",
    "/settings/integrations",
    "/settings/company-profile",
    "/settings/products-services",
    "/settings/subscription",
    "/settings/action-log",
    "/users",
  ];

  const isInsideSettings = settingsPaths.some((p) =>
    location.pathname.startsWith(p)
  );

  if (isInsideSettings) {
    setOpenSettings(true);
  } else {
    setOpenSettings(false);
  }
}, [location.pathname]);

  React.useEffect(() => {
    const onOpenSettingsForTour = () => setOpenSettings(true);
    window.addEventListener(OPEN_SETTINGS_FOR_TOUR_EVENT, onOpenSettingsForTour);
    return () =>
      window.removeEventListener(OPEN_SETTINGS_FOR_TOUR_EVENT, onOpenSettingsForTour);
  }, []);

  const isActive = (path) => {
  // Exact match
  if (location.pathname === path) return true;

  // Special case: allow nesting ONLY if explicitly desired
  if (
    path !== "/" &&
    location.pathname.startsWith(path + "/") &&
    path !== "/leads" // 👈 prevent parent highlighting
  ) {
    return true;
  }

  return false;
};

  const canSeeSettingsSection =
    role === "Admin" || role === "Sales Director" || role === "Manager";

  const handleSettingsToggle = () => {
    if (openSettings) {
      setOpenSettings(false);
      if (isTourOpen) endTour();
      return;
    }

    setOpenSettings(true);

    if (!canSeeSettingsSection || isTourOpen || isSettingsTourComplete()) {
      return;
    }

    setTimeout(() => {
      const steps = resolveSettingsTourSteps(role, {
        canAccessRoleSettings: canAccessRoleSettings,
      });
      if (steps?.length > 0) {
        startTour(steps, SETTINGS_TOUR_KEY);
      }
    }, 450);
  };

 // Function to handle sidebar navigation with tour
const handleSidebarClick = (path, tourSteps, tourKey = null) => {
  // Navigate first
  navigate(path);
  
  // If tour key is provided, check localStorage first
  if (tourKey && localStorage.getItem(tourKey)) {
    return; // Tour already completed, don't start
  }
  
  // Then start tour after a short delay to ensure page is loaded
  setTimeout(() => {
    if (!tourSteps) return;
    
    // Resolve steps if it's a function
    const tourOptions = {
      canAccessRoleSettings:
        user?.is_superuser === true ||
        (role === "Admin" && userIsLegacyDefaultOrganization(user)),
    };
    const steps =
      typeof tourSteps === "function" ? tourSteps(role, tourOptions) : tourSteps;
    
    // Check if we have valid steps
    if (steps && Array.isArray(steps) && steps.length > 0) {
      // Debug: log steps to console
      console.log('Starting tour with steps:', steps);
      console.log('First step:', steps[0]);
      startTour(steps, tourKey); // Pass the key if provided
    } else {
      console.warn('No valid steps found for tour:', steps);
    }
  }, 500);
};

  const runFirstVisitTour = (path, tourSteps, tourKey) => {
    if (location.pathname === path) return;
    if (localStorage.getItem(tourKey)) {
      handleSidebarClick(path, null, null);
    } else {
      handleSidebarClick(path, tourSteps, tourKey);
    }
  };

  const canViewUsers = role === "Admin" || role === "Sales Director";
  const canViewTerritories = role !== "Sales Rep";
  const canViewSettings = can.canAccessSettings;
  const canAccessRoleSettings =
    user?.is_superuser === true ||
    (role === "Admin" && userIsLegacyDefaultOrganization(user));
  const userInitials = getUserInitials(user, "U");
  const userDisplayName =
    user?.full_name?.trim() ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.username ||
    "Unknown user";
  const roleDisplay = role || "Guest";

  return (
    <Box
      sx={{
        width: drawerWidth,
        backgroundColor: sidebarBg,
        color: sidebarText,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: isLight ? "1px solid rgba(0,0,0,0.08)" : "none",
        boxShadow: isLight ? "1px 0 4px rgba(0,0,0,0.04)" : "none",
        pt: 1,
        "& .MuiListItemIcon-root": {
          color: "inherit",
        },
      }}
    >
      <Toolbar />

      <Box
        sx={{
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
          px: 1,
          pb: 2,
        }}
      >
        <List sx={{ pt: 1 }}>

          {/* ------------------------------------------------ Dashboard */}
          <ListItem disablePadding>
            <NavLink to="/dashboard" style={linkStyle}>
              <ListItemButton
                sx={{
                  ...baseItemSx,
                  ...(isActive("/dashboard") ? activeButtonSx : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LayoutDashboard size={20} strokeWidth={1.75} />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </NavLink>
          </ListItem>

          <ListItem disablePadding>
            <NavLink
              to="/sales-pipeline"
              style={linkStyle}
              onClick={() =>
                runFirstVisitTour(
                  "/sales-pipeline",
                  SALES_PIPELINE_TOUR_STEPS,
                  "v1_5_sales_pipeline_tour_complete"
                )
              }
            >
              <ListItemButton
                data-tour="sidebar-sales-pipeline"
                sx={{
                  ...baseItemSx,
                  ...(isActive("/sales-pipeline") ? activeButtonSx : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <TrendingUp size={20} strokeWidth={1.75} />
                </ListItemIcon>
                <ListItemText primary="Sales pipeline" />
              </ListItemButton>
            </NavLink>
          </ListItem>

          {/* ------------------------------------------------ Territories */}
          {canViewTerritories && (
            <ListItem disablePadding>
              <NavLink to="/territories" style={linkStyle}
                onClick={() => {
                  if (location.pathname !== "/territories") {
                    const fullKey = "v1_5_territories_tour_complete";
                    const newKey = "v1_6_territories_new_complete";
                    if (localStorage.getItem(newKey)) {
                      handleSidebarClick("/territories", null, null);
                    } else if (localStorage.getItem(fullKey)) {
                      const newSteps = TERRITORIES_NEW_STEPS(role);
                      if (newSteps && newSteps.length > 0) {
                        handleSidebarClick("/territories", newSteps, newKey);
                      } else {
                        handleSidebarClick("/territories", null, null);
                      }
                    } else {
                      handleSidebarClick("/territories", TERRITORIES_TOUR_STEPS, fullKey);
                    }
                  }
                }}
              >
        <ListItemButton
          data-tour="sidebar-territories"
                  sx={{
                    ...baseItemSx,
                    ...(isActive("/territories") ? activeButtonSx : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <MapIcon size={20} strokeWidth={1.75} />
                  </ListItemIcon>
                  <ListItemText primary="Territories" />
                </ListItemButton>
              </NavLink>
            </ListItem>
          )}

        

          {/* ------------------------------------------------ Leads Map */}
          <ListItem disablePadding>
            <NavLink to="/leads" style={linkStyle}
              onClick={() => {
                if (location.pathname !== "/leads") {
                  const isMobile = window.innerWidth <= 768;
                  const fullKey = isMobile ? "v1_5_leads_mobile_map_tour_complete" : "v1_5_leads_map_tour_complete";
                  const newKey = isMobile ? "v1_6_leads_mobile_map_new_complete" : "v1_6_leads_map_new_complete";
                  if (localStorage.getItem(newKey)) {
                    handleSidebarClick("/leads", null, null);
                  } else if (localStorage.getItem(fullKey)) {
                    const newSteps = isMobile ? LEADS_MOBILE_MAP_NEW_STEPS(role) : LEADS_MAP_NEW_STEPS(role);
                    if (newSteps && newSteps.length > 0) {
                      handleSidebarClick("/leads", newSteps, newKey);
                    } else {
                      handleSidebarClick("/leads", null, null);
                    }
                  } else {
                    const steps = isMobile ? LEADS_MOBILE_MAP_TOUR_STEPS(role) : LEADS_MAP_TOUR_STEPS(role);
                    handleSidebarClick("/leads", steps, fullKey);
                  }
                }
              }}
            >
    <ListItemButton
      data-tour="sidebar-leads-map"
                sx={{
                  ...baseItemSx,
                  ...(isActive("/leads") ? activeButtonSx : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Building2 size={20} strokeWidth={1.75} />
                </ListItemIcon>
                <ListItemText primary="Leads Map" />
              </ListItemButton>
            </NavLink>
          </ListItem>

{/* ------------------------------------------------ Follow-Up Intelligence */}
          {canViewTerritories && (
            <ListItem disablePadding>
              <NavLink
                to="/follow-up-intelligence"
                style={linkStyle}
                onClick={() =>
                  runFirstVisitTour(
                    "/follow-up-intelligence",
                    FOLLOW_UP_INTEL_TOUR_STEPS,
                    "v1_5_follow_up_intel_tour_complete"
                  )
                }
              >
                <ListItemButton
                  data-tour="sidebar-follow-up-intel"
                  sx={{
                    ...baseItemSx,
                    ...(isActive("/follow-up-intelligence") ? activeButtonSx : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {followUpIntelOverdue > 0 ? (
                      <Badge
                        color="error"
                        badgeContent={followUpIntelOverdue}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: 11,
                            height: 18,
                            minWidth: 18,
                            right: -2,
                            top: 2,
                          },
                        }}
                      >
                        <Target size={20} strokeWidth={1.75} />
                      </Badge>
                    ) : (
                      <Target size={20} strokeWidth={1.75} />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Follow-Up Intel" />
                </ListItemButton>
              </NavLink>
            </ListItem>
          )}

{/* ------------------------------------------------ Search Leads */}
<ListItem disablePadding>
  <NavLink to="/leads/search" style={linkStyle}
    onClick={() => {
      if (location.pathname !== "/leads/search") {
        const fullKey = "v1_5_search_leads_tour_complete";
        const newKey = "v1_6_search_leads_new_complete";
        if (localStorage.getItem(newKey)) {
          handleSidebarClick("/leads/search", null, null);
        } else if (localStorage.getItem(fullKey)) {
          const newSteps = SEARCH_LEADS_NEW_STEPS(role);
          if (newSteps && newSteps.length > 0) {
            handleSidebarClick("/leads/search", newSteps, newKey);
          } else {
            handleSidebarClick("/leads/search", null, null);
          }
        } else {
          handleSidebarClick("/leads/search", SEARCH_LEADS_TOUR_STEPS, fullKey);
        }
      }
    }}
  >
    <ListItemButton
data-tour="sidebar-search-leads"
      sx={{
        ...baseItemSx,
        ...(isActive("/leads/search") ? activeButtonSx : {}),
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Search size={20} strokeWidth={1.75} />
      </ListItemIcon>
      <ListItemText primary="Search Leads" />
    </ListItemButton>
  </NavLink>
</ListItem>
           

{/* ------------------------------------------------ Message Center */}
<ListItem disablePadding>
  <NavLink
    to="/message-center"
    style={linkStyle}
    onClick={() =>
      runFirstVisitTour(
        "/message-center",
        MESSAGE_CENTER_TOUR_STEPS,
        "v1_5_message_center_tour_complete"
      )
    }
  >
  <ListItemButton
    data-tour="sidebar-message-center"
    sx={{
      ...baseItemSx,
      ...(isActive("/message-center") ? activeButtonSx : {}),
    }}
  >
    <ListItemIcon sx={{ minWidth: 36 }}>
  {messageCenterUnread > 0 ? (
    <Badge
      color="error"
      badgeContent={messageCenterUnread}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: 11,
          height: 18,
          minWidth: 18,
          right: -2,
          top: 2,
        },
      }}
    >
      <Mail size={20} strokeWidth={1.75} />
    </Badge>
  ) : (
    <Mail size={20} strokeWidth={1.75} />
  )}
</ListItemIcon>

    <ListItemText primary="Message Center" />
  </ListItemButton>
</NavLink>

</ListItem>

          {/* ------------------------------------------------ Conversations */}
          <ListItem disablePadding>
            <NavLink
              to="/conversations"
              style={linkStyle}
              onClick={() =>
                runFirstVisitTour(
                  "/conversations",
                  CONVERSATIONS_TOUR_STEPS,
                  "v1_5_conversations_tour_complete"
                )
              }
            >
              <ListItemButton
                data-tour="sidebar-live-sms"
                sx={{
                  ...baseItemSx,
                  ...(isActive("/conversations") ? activeButtonSx : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {conversationsUnread > 0 ? (
                    <Badge
                      color="error"
                      badgeContent={conversationsUnread}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: 11,
                          height: 18,
                          minWidth: 18,
                          right: -2,
                          top: 2,
                        },
                      }}
                    >
                      <MessagesSquare size={20} strokeWidth={1.75} />
                    </Badge>
                  ) : (
                    <MessagesSquare size={20} strokeWidth={1.75} />
                  )}
                </ListItemIcon>
                <ListItemText primary="Live SMS" />
              </ListItemButton>
            </NavLink>
          </ListItem>

          {/* ------------------------------------------------ Team Chat */}
          <ListItem disablePadding>
            <NavLink to="/team-chat" style={linkStyle}
              onClick={() => {
                if (location.pathname !== "/team-chat") {
                  const fullKey = "v1_5_team_chat_tour_complete";
                  const newKey = "v1_6_team_chat_new_complete";
                  if (localStorage.getItem(newKey)) {
                    handleSidebarClick("/team-chat", null, null);
                  } else if (localStorage.getItem(fullKey)) {
                    const newSteps = TEAM_CHAT_NEW_STEPS(role);
                    if (newSteps && newSteps.length > 0) {
                      handleSidebarClick("/team-chat", newSteps, newKey);
                    } else {
                      handleSidebarClick("/team-chat", null, null);
                    }
                  } else {
                    handleSidebarClick("/team-chat", TEAM_CHAT_TOUR_STEPS, fullKey);
                  }
                }
              }}
            >
              <ListItemButton
                data-tour="sidebar-team-chat"
                sx={{
                  ...baseItemSx,
                  ...(isActive("/team-chat") ? activeButtonSx : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {teamChatUnread > 0 ? (
                    <Badge
                      color="error"
                      badgeContent={teamChatUnread}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: 11,
                          height: 18,
                          minWidth: 18,
                          right: -2,
                          top: 2,
                        },
                      }}
                    >
                      <MessageCircle size={20} strokeWidth={1.75} />
                    </Badge>
                  ) : (
                    <MessageCircle size={20} strokeWidth={1.75} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Team Chat & Support"
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </NavLink>
          </ListItem>


{/* -------------------------- Rep Positions (Admin, Sales Director, Manager) */}
                  {(role === "Admin" || role === "Sales Director" || role === "Manager") && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/rep-positions"
                        style={linkStyle}
                        onClick={() =>
                          runFirstVisitTour(
                            "/rep-positions",
                            REP_POSITIONS_TOUR_STEPS,
                            "v1_5_rep_positions_tour_complete"
                          )
                        }
                      >
                        <ListItemButton
                          data-tour="sidebar-rep-positions"
                          sx={{
                            ...baseItemSx,
                        
                            ...(isActive("/rep-positions") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <MapPin size={20} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Rep Positions" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}

{/* ------------------------------------------------ What's New */}
<ListItem disablePadding>
  <NavLink
    to="/changelog"
    style={linkStyle}
    onClick={() =>
      runFirstVisitTour("/changelog", CHANGELOG_TOUR_STEPS, "v1_5_changelog_tour_complete")
    }
  >
    <ListItemButton
      data-tour="sidebar-whats-new"
      sx={{
        ...baseItemSx,
        ...(isActive("/changelog") ? activeButtonSx : {}),
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Sparkles size={20} strokeWidth={1.75} />
      </ListItemIcon>
      <ListItemText primary="What's New" />
    </ListItemButton>
  </NavLink>
</ListItem>

          {/* ------------------------------------------------ Settings (Admin, Sales Director & Manager) */}
          {(role === "Admin" || role === "Sales Director" || role === "Manager") && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  data-tour="sidebar-settings"
                  sx={{
                    ...baseItemSx,
                    ...(isActive("/settings") ? activeButtonSx : {}),
                  }}
                  onClick={handleSettingsToggle}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Settings size={20} strokeWidth={1.75} />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                  {openSettings ? (
                    <ChevronUp size={18} strokeWidth={2} />
                  ) : (
                    <ChevronDown size={18} strokeWidth={2} />
                  )}
                </ListItemButton>
              </ListItem>

              <Collapse in={openSettings} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ ml: 2 }}>
                  
                  {/* ----------------------------------------------------Company Profile (Admin & Sales Director) */}
                  {(role === "Admin" || role === "Sales Director") && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/settings/company-profile"
                        style={linkStyle}
                        onClick={() =>
                          runFirstVisitTour(
                            "/settings/company-profile",
                            COMPANY_PROFILE_TOUR_STEPS,
                            "v1_5_company_profile_tour_complete"
                          )
                        }
                      >
                        <ListItemButton
                          data-tour="sidebar-company-profile"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/settings/company-profile") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <FileText size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Company Profile" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}

                  {/* ----------------------------------------------------Products & Services (Admin & Sales Director) */}
                  {(role === "Admin" || role === "Sales Director") && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/settings/products-services"
                        style={linkStyle}
                        onClick={() => {
                          if (location.pathname !== "/settings/products-services") {
                            const fullKey = "v1_5_products_services_tour_complete";
                            const newKey = "v1_6_products_services_new_complete";
                            if (localStorage.getItem(newKey)) {
                              handleSidebarClick("/settings/products-services", null, null);
                            } else if (localStorage.getItem(fullKey)) {
                              const newSteps = PRODUCTS_SERVICES_NEW_STEPS(role);
                              if (newSteps && newSteps.length > 0) {
                                handleSidebarClick("/settings/products-services", newSteps, newKey);
                              } else {
                                handleSidebarClick("/settings/products-services", null, null);
                              }
                            } else {
                              handleSidebarClick("/settings/products-services", PRODUCTS_SERVICES_TOUR_STEPS, fullKey);
                            }
                          }
                        }}
                      >
                        <ListItemButton
                          data-tour="sidebar-products-services"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/settings/products-services") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Package size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Products & Services" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}


                  {/* ----------------------------------------------------Roles & Groups (Admin & Sales Director) */}
                  {canAccessRoleSettings && (
                    <ListItem disablePadding>
                      <NavLink to="/settings/roles" style={linkStyle}>
                        <ListItemButton
                          data-tour="sidebar-roles"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/settings/roles") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                             <ShieldCheck size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Roles & Groups" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}

                  {/* -------------------------- Users Management (Admin & Sales Director) */}
                  {(role === "Admin" || role === "Sales Director") && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/users"
                        style={linkStyle}
                        onClick={() =>
                          runFirstVisitTour(
                            "/users",
                            USERS_TOUR_STEPS,
                            "v1_5_users_tour_complete"
                          )
                        }
                      >
                        <ListItemButton
                          data-tour="sidebar-users"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/users") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Users size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Users" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}


                  {/* ---------------------------------------------------------------------------------------Reports (Admin, Sales Director & Manager) */}
                  {(role === "Admin" || role === "Sales Director" || role === "Manager") && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/settings/reports"
                        style={linkStyle}
                        onClick={() =>
                          runFirstVisitTour(
                            "/settings/reports",
                            REPORTS_TOUR_STEPS,
                            "v1_5_reports_tour_complete"
                          )
                        }
                      >
                        <ListItemButton
                          data-tour="sidebar-reports"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/settings/reports") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <BarChart3 size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Reports" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}

  {/* ----------------------------------------------------Subscriptions (Admin only) */}
                  {role === "Admin" && (
                    <ListItem disablePadding>
                      <NavLink
                        to="/settings/subscription"
                        style={linkStyle}
                        onClick={() =>
                          runFirstVisitTour(
                            "/settings/subscription",
                            SUBSCRIPTION_TOUR_STEPS,
                            "v1_5_subscription_tour_complete"
                          )
                        }
                      >
                        <ListItemButton
                          data-tour="sidebar-subscription"
                          sx={{
                            ...baseItemSx,
                            pl: 3,
                            ...(isActive("/settings/subscription") ? activeButtonSx : {}),
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CreditCard size={18} strokeWidth={1.75} />
                          </ListItemIcon>
                          <ListItemText primary="Subscriptions" />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  )}
{/* -------------------------- Integrations (Admin only) */}
{role === "Admin" && (
  <ListItem disablePadding>
    <NavLink
      to="/settings/integrations"
      style={linkStyle}
      onClick={() =>
        runFirstVisitTour(
          "/settings/integrations",
          INTEGRATIONS_TOUR_STEPS,
          "v1_5_integrations_tour_complete"
        )
      }
    >
      <ListItemButton
        data-tour="sidebar-integrations"
        sx={{
          ...baseItemSx,
          pl: 3,
          ...(isActive("/settings/integrations") ? activeButtonSx : {}),
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Share2 size={18} strokeWidth={1.75} />
        </ListItemIcon>
        <ListItemText primary="Integrations" />
      </ListItemButton>
    </NavLink>
  </ListItem>
)}



		{/* ----------------------------------------------------------------------------------Action Log */}
{(role === "Admin" || role === "Sales Director") && (
  <ListItem disablePadding>
    <NavLink
      to="/settings/action-log"
      style={linkStyle}
      onClick={() =>
        runFirstVisitTour(
          "/settings/action-log",
          ACTION_LOG_TOUR_STEPS,
          "v1_5_action_log_tour_complete"
        )
      }
    >
      <ListItemButton
        data-tour="sidebar-action-log"
        sx={{
          ...baseItemSx,
          pl: 3,
          ...(isActive("/settings/action-log") ? activeButtonSx : {}),
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <History size={18} strokeWidth={1.75} />
        </ListItemIcon>
        <ListItemText primary="Action Log" />
      </ListItemButton>
    </NavLink>
  </ListItem>
)}



                  
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Box>

      <Box
        sx={{
          borderTop: isLight
            ? "1px solid rgba(0,0,0,0.08)"
            : "1px solid rgba(255,255,255,0.12)",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          backgroundColor: sidebarBg,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: 13,
            flexShrink: 0,
            textTransform: "uppercase",
          }}
        >
          {userInitials}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {userDisplayName}
          </Box>
          <Box
            sx={{
              fontSize: 12,
              opacity: 0.75,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {roleDisplay}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
